import { CsRecipeInput, RecipeSchemaContext } from './types';
import { HIDDEN_IN_PROMPT } from './validation';
import { LENS_PRESETS, NAMED_CAMERA_ANGLES } from '../canvas/camera-angles';

/**
 * Room fields injected into surface Product Photography ("Room Scene"). The
 * seed has no room fields for this combination, so they're supplied here and
 * gated per method by the caller:
 *   In-Room (studio) · Custom → roomType + style
 *   Lifestyle                 → surface + environment
 */
const ROOM_TYPE_OPTIONS = [
    { value: 'living-room', label: 'Living room' },
    { value: 'bedroom', label: 'Bedroom' },
    { value: 'kitchen', label: 'Kitchen' },
    { value: 'bathroom', label: 'Bathroom' },
    { value: 'office', label: 'Home office' },
    { value: 'dining-room', label: 'Dining room' },
    { value: 'hallway', label: 'Hallway / entryway' },
    { value: 'outdoor', label: 'Outdoor / patio' },
    { value: 'custom', label: 'Custom (describe it)' },
];

const STYLE_OPTIONS = [
    { value: 'modern-minimal', label: 'Modern minimal' },
    { value: 'scandinavian', label: 'Scandinavian' },
    { value: 'japandi', label: 'Japandi' },
    { value: 'wabi-sabi', label: 'Wabi-sabi' },
    { value: 'mid-century', label: 'Mid-century' },
    { value: 'industrial', label: 'Industrial' },
    { value: 'boho', label: 'Boho' },
    { value: 'mediterranean', label: 'Mediterranean' },
    { value: 'coastal', label: 'Coastal' },
    { value: 'art-deco', label: 'Art deco' },
    { value: 'custom', label: 'Custom (describe it)' },
];

/** Environments are prefixed by surface so only matching ones are offered. */
/**
 * Scene recipes that accept an inspiration picture.
 *
 * from-reference is absent on purpose: it already has `styleReferenceUrl`, and
 * that one is required rather than optional — reproducing one specific shot is
 * its whole premise, not a hint layered on top of a scene.
 */
export const SCENE_RECIPES_WITH_INSPIRATION = new Set([
    'lifestyle-scene',
    'lifestyle-room',
    'product-closeup',
]);

const ENVIRONMENT_OPTIONS = [
    { value: 'floor-living-room', label: 'Living room' },
    { value: 'floor-bedroom', label: 'Bedroom' },
    { value: 'floor-kitchen', label: 'Kitchen' },
    { value: 'floor-bathroom', label: 'Bathroom' },
    { value: 'floor-hallway', label: 'Hallway' },
    { value: 'floor-entryway', label: 'Entryway' },
    { value: 'floor-terrace', label: 'Terrace / patio' },
    { value: 'floor-poolside', label: 'Poolside deck' },
    { value: 'floor-garden-path', label: 'Garden path' },
    { value: 'floor-entrance-outdoor', label: 'Outdoor entrance' },
    { value: 'wall-kitchen-backsplash', label: 'Kitchen backsplash' },
    { value: 'wall-bathroom-vanity', label: 'Bathroom vanity' },
    { value: 'wall-bathroom-shower', label: 'Bathroom shower' },
    { value: 'wall-fireplace-surround', label: 'Fireplace surround' },
    { value: 'wall-feature-wall', label: 'Feature wall' },
    { value: 'wall-exterior-facade', label: 'Exterior facade' },
    { value: 'wall-outdoor-kitchen', label: 'Outdoor kitchen backsplash' },
];

/**
 * Room Scene · Lifestyle submits under lifestyle-scene, which accepts only
 * these lighting values. Restricting the field keeps an invalid value from
 * ever being sent.
 */
const LIFESTYLE_LIGHTING_OPTIONS = [
    { value: 'window-natural', label: 'Natural window light' },
    { value: 'overhead', label: 'Overhead key light' },
    { value: 'soft-diffuse', label: 'Soft diffuse' },
    { value: 'golden-hour', label: 'Warm golden hour' },
    { value: 'cinematic', label: 'Cinematic' },
    { value: 'evening', label: 'Evening, lamps on' },
];

/**
 * Inputs the vendor should see, with each select's options filtered the same
 * way — so a surface-only backdrop never appears for a furniture product.
 *
 * `appliesTo` is the primary rule; surface Product Photography additionally
 * gets the injected room fields above.
 */
export function resolveVisibleInputs(ctx: RecipeSchemaContext): CsRecipeInput[] {
    const { recipe, inputs, productVisualizationType: vt, productApplicationTypes } = ctx;
    const all = recipe.inputs || [];

    const matches = (appliesTo?: string[]) =>
        !appliesTo || appliesTo.length === 0 || (!!vt && appliesTo.includes(vt));

    const filtered: CsRecipeInput[] = all
        .filter((inp) => matches(inp.appliesTo))
        .filter((inp) => !inp.showWhen
            || inp.showWhen.equals.includes(String(inputs?.[inp.showWhen.key] ?? '')))
        .map((inp) => {
            if (!inp.options || inp.options.length === 0) return inp;
            const opts = inp.options.filter((o) => matches(o.appliesTo));
            if (opts.length === inp.options.length) return inp;
            return { ...inp, options: opts };
        });

    const has = (key: string) => filtered.some((i) => i.key === key);

    // The camera widget's ranges live in CameraControl3D and nowhere the schema
    // could see, so a caller setting these by value — the planner — had nothing
    // to aim at and nothing stopping an elevation of 200.
    const CAMERA_BOUNDS: Record<string, { min: number; max: number; step: number }> = {
        azimuth: { min: 0, max: 360, step: 5 },
        elevation: { min: -30, max: 90, step: 5 },
        distance: { min: 0, max: 10, step: 0.5 },
    };
    for (let i = 0; i < filtered.length; i += 1) {
        const bounds = CAMERA_BOUNDS[filtered[i].key];
        if (bounds && filtered[i].min === undefined) filtered[i] = { ...filtered[i], ...bounds };
    }

    // The angle to pose the 3D studio at. Declared so it survives coercion and
    // so the agent has a vocabulary to ask with — the camera itself is a
    // position and a target, which is not something to name a shot with.
    if (inputs.use3dCamera === true || inputs.use3dCamera === 'true') {
        if (!has('cameraAngle')) {
            filtered.push({
                key: 'cameraAngle',
                label: 'Camera angle',
                type: 'select',
                required: false,
                agentOnly: true,
                default: '',
                options: Object.entries(NAMED_CAMERA_ANGLES).map(([value, spec]) => ({
                    value, label: spec.label,
                })),
            });
        }
        // The lens, for the same reason as the angle.
        //
        // The 3D studio writes `lensMm` when it captures, so the value exists
        // on the node but no recipe declares it — which made it unsettable.
        // Asked to zoom in, the agent did the right thing and was told
        // "lensMm cannot be set on this node as it is currently configured".
        // Zooming *is* the lens; without it the only vocabulary left is a
        // named angle, and none of them mean "closer".
        if (!has('lensMm')) {
            filtered.push({
                key: 'lensMm',
                label: 'Lens',
                type: 'select',
                required: false,
                agentOnly: true,
                default: '50',
                // Option values are strings; the studio reads the number back.
                options: LENS_PRESETS.map((l) => ({
                    value: String(l.mm),
                    label: `${l.mm}mm · ${l.label}`,
                })),
            });
        }
        // The measured camera behind a reference photo, as JSON:
        // {"heightM":1.21,"pitchDeg":0.4,"rollDeg":0,"lensMm":20}
        //
        // A named angle is a bucket twenty degrees wide; this is the shot the
        // reference was actually taken with. When it is set the studio poses
        // to these numbers and ignores `cameraAngle`, which exists for the
        // case where there is no reference to measure.
        if (!has('cameraView')) {
            filtered.push({
                key: 'cameraView',
                label: 'Measured camera',
                type: 'text',
                required: false,
                default: '',
                agentOnly: true,
            });
        }
        if (!has('aperture')) {
            filtered.push({
                key: 'aperture',
                label: 'Aperture',
                type: 'number',
                required: false,
                agentOnly: true,
                default: 5.6,
                min: 1.4,
                max: 22,
            });
        }
    }

    /**
     * Product Photography · Prompt — the brief answers what the rows ask.
     *
     * Filtered HERE rather than in the card, which is where its siblings live:
     * the card's own hide-list is a second copy of the validator's, kept in step
     * by hand and commented as such. One more rule in two places is one more
     * chance for them to drift, and `resolveVisibleInputs` is what the card
     * renders from anyway.
     */
    const mode = (ctx.inputs?.mode ?? '').toString();
    if (recipe.slug === 'product-closeup' && mode === 'prompt') {
        for (let i = filtered.length - 1; i >= 0; i -= 1) {
            if (HIDDEN_IN_PROMPT.has(filtered[i].key)) filtered.splice(i, 1);
        }
    }

    // The scene text in Prompt mode. The card renders it straight from
    // `inputs.sceneDetails` and the validator requires it, but no recipe
    // declares it — so anything setting inputs programmatically had it thrown
    // away as an unknown key. The planner wrote a whole scene and the canvas
    // kept only the aspect-ratio change that happened to sit beside it.
    /**
     * Product Photography offers furniture a Prompt tab too.
     *
     * The tab was there — `MODE_TABS_BY_SLUG` lists it for every product type —
     * but the field behind it was surface-only, so a vendor picking Prompt on a
     * chair got the Custom option list instead of somewhere to write. There was
     * nothing to type into and no sign why.
     */
    const takesSceneText = (recipe.slug === 'lifestyle-scene' && vt === 'model3d')
        || recipe.slug === 'product-closeup';
    if (takesSceneText && !has('sceneDetails')) {
        filtered.push({
            key: 'sceneDetails', label: 'Describe the scene', type: 'textarea',
            required: false, default: '',
        });
    }

    // The reference recipe's one real input. No seed declares it, so a plan
    // that set it had it thrown away as unknown — which made the recipe the
    // agent could not use. This is the recipe that reproduces the brand's own
    // photography around a product, so it has to be reachable from a plan.
    //
    // Declared but never shown. The card already has a real control for this:
    // an upload with a preview and a remove button, which is how a person
    // chooses a picture. Rendering the schema entry as well put a raw URL box
    // beside it — two controls for one value, and the worse of the two asking
    // the vendor to paste an S3 address. Same reasoning as `inspirationUrl`
    // below, which was declared this way from the start.
    if (recipe.slug === 'from-reference' && !has('styleReferenceUrl')) {
        filtered.push({
            key: 'styleReferenceUrl', label: 'Reference picture', type: 'text',
            required: true, default: '',
        });
    }
    // ...and it never gets a field, however it was declared. Marking only the
    // synthesized one would leave a seed free to put the URL box back, which
    // is the kind of thing that comes back years later as a mystery.
    if (recipe.slug === 'from-reference') {
        for (let i = 0; i < filtered.length; i += 1) {
            if (filtered[i].key === 'styleReferenceUrl') {
                filtered[i] = { ...filtered[i], agentOnly: true };
            }
        }
    }

    // Inspiration: a picture the vendor liked, on any scene recipe.
    //
    // An input rather than a mode, deliberately. The same field then works
    // whether a person pasted it or the agent chose it, and it survives the
    // move to a chat-first studio instead of being rebuilt by it. Optional
    // everywhere — from-reference above is the one recipe that cannot work
    // without one, and declares its own.
    if (SCENE_RECIPES_WITH_INSPIRATION.has(recipe.slug) && !has('inspirationUrl')) {
        filtered.push({
            key: 'inspirationUrl', label: 'Inspiration picture', type: 'text',
            required: false, default: '',
            // Set by the agent from a picture attached in the chat, never
            // typed into the card: a raw URL field on a recipe is nobody's
            // idea of choosing an inspiration.
            agentOnly: true,
        });
    }

    if (recipe.slug !== 'product-closeup' || vt !== 'surface') return filtered;

    if (!has('roomType')) {
        filtered.push({
            key: 'roomType', label: 'Environment', type: 'select', required: false,
            default: 'living-room', options: ROOM_TYPE_OPTIONS,
        });
    }
    if (!has('style')) {
        filtered.push({
            key: 'style', label: 'Style', type: 'select', required: false,
            default: 'modern-minimal', options: STYLE_OPTIONS,
        });
    }
    if (!has('styleCustom')) {
        filtered.push({ key: 'styleCustom', label: 'Describe the style', type: 'textarea', required: false, default: '' });
    }
    if (!has('surface')) {
        const applies = productApplicationTypes;
        const unrestricted = !applies || applies.length === 0;
        const allowsFloor = unrestricted || applies!.includes('floor');
        const allowsWalls = unrestricted || applies!.includes('walls') || applies!.includes('wall');
        const surfOpts: Array<{ value: string; label: string }> = [];
        if (allowsFloor) surfOpts.push({ value: 'floor', label: 'Floor' });
        if (allowsWalls) surfOpts.push({ value: 'walls', label: 'Wall' });
        filtered.push({
            key: 'surface', label: 'Surface', type: 'select', required: false,
            default: allowsFloor ? 'floor' : 'walls',
            options: surfOpts.length ? surfOpts : [{ value: 'floor', label: 'Floor' }],
        });
    }
    if (!has('environment')) {
        const wantWall = inputs.surface === 'walls' || inputs.surface === 'wall';
        const surfPrefix = wantWall ? 'wall-' : 'floor-';
        filtered.push({
            key: 'environment', label: 'Environment', type: 'select', required: false,
            default: 'floor-living-room',
            options: [
                ...ENVIRONMENT_OPTIONS.filter((o) => o.value.startsWith(surfPrefix)),
                { value: 'custom', label: 'Custom scene (describe it yourself)' },
            ],
        });
    }
    if (!has('environmentCustom')) {
        filtered.push({ key: 'environmentCustom', label: 'Describe the scene', type: 'textarea', required: false, default: '' });
    }

    const lightingIdx = filtered.findIndex((i) => i.key === 'lighting');
    if (lightingIdx >= 0) {
        filtered[lightingIdx] = { ...filtered[lightingIdx], options: LIFESTYLE_LIGHTING_OPTIONS };
    }

    // In Lifestyle mode Surface is the primary choice, so it reads first —
    // above the AI-picked Environment and Lighting rather than buried after
    // the seeded fields.
    for (const key of ['environmentCustom', 'environment', 'surface']) {
        const idx = filtered.findIndex((i) => i.key === key);
        if (idx >= 0) filtered.unshift(filtered.splice(idx, 1)[0]);
    }

    return filtered;
}
